import { Button } from '@blueprinthq/joy'
import { HStack, Image, useToast, VStack } from '@chakra-ui/react'
import { trackEvent } from '@lib/clinician-tracking'
import { chunk } from 'lodash'
import React, { useRef, useState } from 'react'
import { useMutation } from 'react-query'
import { growsurfControllerGetInviteLink } from '~/clinician-api'
import { FB_APP_ID } from '../../constants/referral'
import { ShareButtonDrawer } from './share-button-drawer'

enum SocialSharingOption {
  EMAIL = 'email',
  MESSAGES = 'messages',
  WHATSAPP = 'whatsapp',
  FACEBOOK = 'facebook',
  LINKEDIN = 'linkedin',
  TWITTER = 'twitter',
  INSTAGRAM = 'instagram',
  MESSENGER = 'messenger'
}

const ICON_URLS = {
  email:
    'https://res.cloudinary.com/hellojoy/image/upload/v1745961515/email_jmrbma.svg',
  facebook:
    'https://res.cloudinary.com/hellojoy/image/upload/v1745961515/facebook_ntyd1n.svg',
  twitter:
    'https://res.cloudinary.com/hellojoy/image/upload/v1745961527/twitter_fwlw3m.svg',
  instagram:
    'https://res.cloudinary.com/hellojoy/image/upload/v1745961515/instagram_kxg3lx.svg',
  linkedin:
    'https://res.cloudinary.com/hellojoy/image/upload/v1745961515/linkedin_al57xw.svg',
  messenger:
    'https://res.cloudinary.com/hellojoy/image/upload/v1746033188/messenger_jl6qse.svg',
  whatsapp:
    'https://res.cloudinary.com/hellojoy/image/upload/v1746033188/whatsapp_tgr1qr.svg',
  messages:
    'https://res.cloudinary.com/hellojoy/image/upload/v1746033184/sms_vs1fjf.svg'
}

export const DEFAULT_SOCIAL_COPY =
  'Therapist friends, I love using Blueprint’s AI Assistant, and I think it might be a great fit for you too! Blueprint automates progress notes and provides insights and recommendations that have helped me get hours of time and focus back every week. So why not give it a try? Use my referral code to get a month of unlimited notes for free.'
const TWITTER_COPY =
  'Therapist friends, I love Blueprint’s AI Assistant, and I think you might too! Blueprint automates progress notes and has helped me get hours of time back every week. Use my referral code to get a free month of unlimited notes.'
const DIRECT_MESSAGE_COPY =
  'Hey there – therapist-to-therapist, I’ve been loving using Blueprint’s AI Assistant, and I think it might be a great fit for you too! Blueprint automates progress notes and provides insights and recommendations that have helped me get hours of time and focus back every week. So why not give it a try? Use my referral code to get a month of unlimited notes for free.'

const getSocialLinks = (
  referralURL: string
): Record<SocialSharingOption, string> => {
  const encodedReferralURL = encodeURIComponent(referralURL)

  return {
    [SocialSharingOption.EMAIL]: '',
    [SocialSharingOption.FACEBOOK]: `https://www.facebook.com/dialog/share?app_id=${FB_APP_ID}&display=popup&href=${encodedReferralURL}&quote=${encodeURIComponent(
      DEFAULT_SOCIAL_COPY
    )}`,
    [SocialSharingOption.TWITTER]: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      TWITTER_COPY
    )}&url=${encodedReferralURL}&via=blueprint_hq`,
    [SocialSharingOption.INSTAGRAM]: `https://www.instagram.com/direct/`,
    [SocialSharingOption.LINKEDIN]: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      referralURL
    )}&text=${encodeURIComponent(DEFAULT_SOCIAL_COPY)}`,
    [SocialSharingOption.MESSENGER]: `http://www.facebook.com/dialog/send?app_id=${FB_APP_ID}&link=${encodedReferralURL}&redirect_uri=https%3A%2F%2Fblueprint.ai&display=page`,
    [SocialSharingOption.WHATSAPP]: `https://wa.me/?text=${encodeURIComponent(
      DIRECT_MESSAGE_COPY + '\n'
    )}${encodedReferralURL}`,
    [SocialSharingOption.MESSAGES]: `sms:?&body=${encodeURIComponent(
      DIRECT_MESSAGE_COPY + '\n'
    )}${encodedReferralURL}`
  }
}

const getSocialButtons = (
  referralURL: string,
  handleEmailOption?: () => void
) => {
  const socialLinks = getSocialLinks(referralURL)
  const encodedReferralURL = encodeURIComponent(referralURL)

  return [
    {
      icon: (
        <Image src={ICON_URLS.email} alt="Email" width="16px" height="16px" />
      ),
      url: null,
      mobileTitle: 'Send in Email',
      mobileURL: '',
      mobileDisplayOrder: 0,
      isMobileOnly: false,
      title: 'Email',
      key: SocialSharingOption.EMAIL,
      onClick: handleEmailOption
    },
    {
      icon: (
        <Image
          src={ICON_URLS.messages}
          alt="Messages"
          width="16px"
          height="16px"
        />
      ),
      url: socialLinks[SocialSharingOption.MESSAGES],
      mobileTitle: 'Send in Messages',
      mobileURL: '',
      mobileDisplayOrder: 1,
      isMobileOnly: true,
      title: '',
      key: SocialSharingOption.MESSAGES
    },
    {
      icon: (
        <Image
          src={ICON_URLS.whatsapp}
          alt="WhatsApp"
          width="16px"
          height="16px"
        />
      ),
      url: socialLinks[SocialSharingOption.WHATSAPP],
      mobileTitle: 'Send in WhatsApp',
      mobileURL: '',
      mobileDisplayOrder: 4,
      isMobileOnly: true,
      title: '',
      key: SocialSharingOption.WHATSAPP
    },
    {
      icon: (
        <Image
          src={ICON_URLS.facebook}
          alt="Facebook"
          width="16px"
          height="16px"
        />
      ),
      url: socialLinks[SocialSharingOption.FACEBOOK],
      mobileTitle: 'Share on Facebook',
      mobileURL: `fb://share?link=${encodeURIComponent(referralURL)}`,
      mobileDisplayOrder: 5,
      isMobileOnly: false,
      title: 'Facebook',
      key: SocialSharingOption.FACEBOOK
    },
    {
      icon: (
        <Image
          src={ICON_URLS.linkedin}
          alt="LinkedIn"
          width="16px"
          height="16px"
        />
      ),
      url: socialLinks[SocialSharingOption.LINKEDIN],
      mobileTitle: 'Share on LinkedIn',
      mobileURL: `linkedin://share?url=${encodeURIComponent(
        referralURL
      )}&text=${encodeURIComponent(DEFAULT_SOCIAL_COPY)}`,
      mobileDisplayOrder: 7,
      isMobileOnly: false,
      title: 'LinkedIn',
      key: SocialSharingOption.LINKEDIN
    },
    {
      icon: (
        <Image
          src={ICON_URLS.twitter}
          alt="Twitter"
          width="16px"
          height="16px"
        />
      ),
      url: socialLinks[SocialSharingOption.TWITTER],
      mobileTitle: 'Share on X (Twitter)',
      mobileURL: '',
      mobileDisplayOrder: 8,
      isMobileOnly: false,
      title: 'Twitter',
      key: SocialSharingOption.TWITTER
    },
    {
      icon: (
        <Image
          src={ICON_URLS.instagram}
          alt="Instagram"
          width="16px"
          height="16px"
        />
      ),
      url: socialLinks[SocialSharingOption.INSTAGRAM],
      mobileTitle: 'Share on Instagram',
      mobileURL: `instagram://direct/inbox`,
      mobileDisplayOrder: 6,
      isMobileOnly: false,
      title: 'Instagram',
      key: SocialSharingOption.INSTAGRAM
    },
    {
      icon: (
        <Image
          src={ICON_URLS.messenger}
          alt="Messenger"
          width="16px"
          height="16px"
        />
      ),
      url: socialLinks.messenger,
      mobileTitle: 'Send in Messenger',
      mobileURL: `fb-messenger://share?link=${encodedReferralURL}`,
      mobileDisplayOrder: 3,
      isMobileOnly: false,
      title: 'Messenger',
      key: SocialSharingOption.MESSENGER
    }
  ]
}

interface SocialSharingModuleProps {
  referralURL: string
  handleCopyLink: (url: string) => void
  handleEmailOption?: () => void
}

export const SocialSharingModule: React.FC<SocialSharingModuleProps> = ({
  referralURL,
  handleEmailOption
}) => {
  const [isButtonDrawerOpen, setIsButtonDrawerOpen] = useState(false)
  const currentSharingOptionRef = useRef<SocialSharingOption>(
    SocialSharingOption.EMAIL
  )
  const handleOpenButtonDrawer = () => setIsButtonDrawerOpen(true)
  const handleCloseButtonDrawer = () => setIsButtonDrawerOpen(false)
  const toast = useToast()

  const buttons = getSocialButtons(referralURL, handleEmailOption)

  const { mutate: fetchReferralLink } = useMutation(
    growsurfControllerGetInviteLink,
    {
      onSuccess: data => {
        if (data.growsurfInviteLink) {
          const links = getSocialLinks(data.growsurfInviteLink)

          if (currentSharingOptionRef.current !== SocialSharingOption.EMAIL) {
            window.open(links[currentSharingOptionRef.current], '_blank')
          }
        }
      },
      onError: error => {
        console.error('Failed to fetch referral link', error)
        toast({
          title: "Couldn't generate referral link",
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    }
  )

  return (
    <>
      {/* Desktop */}
      <VStack
        pt="xsmall"
        display={{ base: 'none', sm: 'unset' }}
        w="100%"
        gap={2}
      >
        {chunk(
          buttons.filter(b => !b.isMobileOnly),
          2
        ).map((pairOfButtons, idx) => (
          <HStack key={idx} spacing={1} w="100%">
            {pairOfButtons.map(({ url, icon, title, key, onClick }) => {
              const isCustomAction = !url
              const commonProps = {
                key,
                margin: '0px',
                variant: 'outline',
                color: 'primary',
                borderRadius: '2px',
                isFullWidth: true,
                borderColor: 'pale_gray',
                leftIcon: icon,
                colorScheme: 'gray',
                _hover: {
                  bg: 'pale_gray'
                },
                _focus: {
                  outline: 'none'
                },
                onClick: (e: React.MouseEvent) => {
                  trackEvent('Referral V3 -> Clicked Share Button', {
                    shareMethod: key,
                    platform: 'desktop'
                  })

                  currentSharingOptionRef.current = key

                  fetchReferralLink(undefined)

                  if (onClick) {
                    onClick()
                    if (isCustomAction) {
                      e.preventDefault()
                    }
                  }
                },
                children: title
              }

              return <Button {...commonProps} as="button" />
            })}
          </HStack>
        ))}
      </VStack>
      {/* Mobile */}
      <VStack marginTop="8px !important">
        <Button
          display={{ base: 'flex', sm: 'none' }}
          size="lg"
          maxWidth="300px"
          isFullWidth
          onClick={handleOpenButtonDrawer}
          bg="white"
          textColor="#282828"
          border="1px solid rgba(55, 60, 65, 0.14)"
        >
          Share
        </Button>
      </VStack>
      <ShareButtonDrawer
        referralURL={referralURL}
        buttons={buttons}
        isOpen={isButtonDrawerOpen}
        onClose={handleCloseButtonDrawer}
      />
    </>
  )
}
